<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Card, Table, TableHead, TableHeadCell, TableBody, TableBodyRow, TableBodyCell,
		Button, Modal, Label, Input, Select, Toggle, Badge, Spinner
	} from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import ProbeMetricsView from '../../components/admin/ProbeMetricsView.svelte';
	import RowAction from '../../components/admin/RowAction.svelte';
	import {
		listProbes, createProbe, updateProbe, deleteProbe, getProbe, setProbeMetrics, testProbe
	} from '../../AppNetwork';
	import type { Probe, ProbeMetric } from '../../AppTypes';
	import { statusLabel, statusText } from '../../statusUtils';

	let probes: Probe[] = [];
	let loading = true;
	let error = '';
	let search = '';

	let showModal = false;
	let editing: Probe | null = null;
	let form = blankForm();
	let metrics: ProbeMetric[] = [];
	let secretKey = '';
	let isSaving = false;
	let testResult: { status: string; error: string | null; responseTimeMs: number; rawStats: unknown; metrics: Record<string, number | string>; discoveredMetrics?: { key: string; label: string; unit: string | null }[] } | null = null;
	let testError = '';
	let testRunning = false;
	let expandedId: number | null = null;

	function toggleExpand(id: number) {
		expandedId = expandedId === id ? null : id;
	}

	function blankForm() {
		return {
			name: '', type: 'json' as 'http' | 'json', targetUrl: '', httpMethod: 'GET',
			secretMode: 'header' as 'header' | 'bearer' | 'query',
			secretParamName: 'X-Probe-Key', intervalSeconds: 60, timeoutMs: 10000, enabled: true
		};
	}

	function formatDate(dateString: string | null): string {
		if (!dateString) return 'Never';
		return new Date(dateString).toLocaleDateString(undefined, {
			month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
		});
	}

	async function loadProbes() {
		loading = true;
		error = '';
		try {
			probes = (await listProbes({ limit: 200, search })).items;
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load probes';
		} finally {
			loading = false;
		}
	}
	onMount(loadProbes);

	function openCreate() {
		editing = null; form = blankForm(); metrics = []; secretKey = ''; testResult = null; testError = ''; error = ''; showModal = true;
	}

	async function openEdit(probe: Probe) {
		testResult = null; testError = ''; error = '';
		const full = await getProbe(probe.id);
		editing = full;
		form = {
			name: full.name, type: full.type, targetUrl: full.targetUrl, httpMethod: full.httpMethod,
			secretMode: full.secretMode, secretParamName: full.secretParamName,
			intervalSeconds: full.intervalSeconds, timeoutMs: full.timeoutMs, enabled: full.enabled
		};
		metrics = (full.metrics ?? []).map((m) => ({ ...m }));
		secretKey = '';
		showModal = true;
	}

	async function openDuplicate(probe: Probe) {
		testResult = null; testError = ''; error = '';
		const full = await getProbe(probe.id);
		editing = null; // create a new probe from these values
		form = {
			name: `${full.name} (copy)`, type: full.type, targetUrl: full.targetUrl, httpMethod: full.httpMethod,
			secretMode: full.secretMode, secretParamName: full.secretParamName,
			intervalSeconds: full.intervalSeconds, timeoutMs: full.timeoutMs, enabled: full.enabled
		};
		metrics = (full.metrics ?? []).map((m) => ({ key: m.key, label: m.label, unit: m.unit, enabled: m.enabled }));
		secretKey = ''; // secrets are never returned — re-enter for the copy
		showModal = true;
	}

	function addMetric() {
		metrics = [...metrics, { key: '', label: '', unit: null, enabled: true }];
	}
	function removeMetric(index: number) {
		metrics = metrics.filter((_, i) => i !== index);
	}

	async function save() {
		isSaving = true;
		error = '';
		try {
			const payload: any = { ...form };
			if (secretKey) payload.secretKey = secretKey;
			const id = editing ? (await updateProbe(editing.id, payload)).id : (await createProbe(payload)).id;
			await setProbeMetrics(id, metrics.filter((m) => m.key && m.label));
			showModal = false;
			await loadProbes();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save probe';
		}
		isSaving = false;
	}

	async function runTest() {
		if (!editing) return;
		testRunning = true;
		testError = '';
		testResult = null;
		try {
			// Send the current editor metrics so the test reflects unsaved edits / expressions.
			const result = await testProbe(editing.id, metrics.filter((m) => m.key));
			testResult = {
				status: result.status,
				error: result.error,
				responseTimeMs: result.responseTimeMs,
				rawStats: result.rawStats,
				metrics: result.metrics,
				discoveredMetrics: result.discoveredMetrics
			};
		} catch (e) {
			testError = e instanceof Error ? e.message : 'Test failed';
		}
		testRunning = false;
	}

	async function remove(probe: Probe) {
		if (!confirm(`Delete probe "${probe.name}"?`)) return;
		await deleteProbe(probe.id);
		await loadProbes();
	}
</script>

<PageHeader title="Probes" description="Outbound checks that poll your services' stats endpoints." />

<div class="space-y-6">
	<Card class="max-w-none">
		<div class="flex flex-wrap items-center gap-4">
			<div class="flex-1 min-w-[200px]">
				<Input bind:value={search} placeholder="Search probes..." on:keypress={(e) => e.key === 'Enter' && loadProbes()}>
					<i slot="left" class="fa-solid fa-search text-gray-400"></i>
				</Input>
			</div>
			<Button on:click={openCreate}>
				<i class="fa-solid fa-plus me-2"></i>New Probe
			</Button>
		</div>
	</Card>

	<Card padding="none" class="max-w-none">
		{#if loading}
			<div class="p-8 text-center">
				<i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
				<p class="text-gray-500 dark:text-gray-400">Loading probes...</p>
			</div>
		{:else if error}
			<div class="p-8 text-center">
				<i class="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">{error}</p>
				<Button class="mt-4" on:click={loadProbes}>Retry</Button>
			</div>
		{:else if probes.length === 0}
			<div class="p-8 text-center">
				<i class="fa-solid fa-satellite-dish text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">No probes yet</p>
			</div>
		{:else}
			<Table striped divClass="relative overflow-x-auto rounded-lg">
				<TableHead class="bg-gray-50 dark:bg-gray-700">
					<TableHeadCell class="w-8"><span class="sr-only">Expand</span></TableHeadCell>
					<TableHeadCell>Name</TableHeadCell>
					<TableHeadCell>Type</TableHeadCell>
					<TableHeadCell>Target</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Last Run</TableHeadCell>
					<TableHeadCell>Enabled</TableHeadCell>
					<TableHeadCell>Actions</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each probes as probe}
						<TableBodyRow>
							<TableBodyCell class="pr-0">
								<button
									type="button"
									class="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1"
									aria-label="Toggle metrics"
									on:click={() => toggleExpand(probe.id)}
								>
									<i class="fa-solid fa-chevron-right transition-transform {expandedId === probe.id ? 'rotate-90' : ''}"></i>
								</button>
							</TableBodyCell>
							<TableBodyCell class="font-medium text-gray-900 dark:text-white">{probe.name}</TableBodyCell>
							<TableBodyCell><Badge color="dark">{probe.type === 'http' ? 'HTTP' : 'JSON'}</Badge></TableBodyCell>
							<TableBodyCell class="max-w-xs truncate text-gray-500 dark:text-gray-400">{probe.targetUrl}</TableBodyCell>
							<TableBodyCell><span class={statusText(probe.currentStatus)}>{statusLabel(probe.currentStatus)}</span></TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400">{formatDate(probe.lastRunAt)}</TableBodyCell>
							<TableBodyCell>
								{#if probe.enabled}<Badge color="green">On</Badge>{:else}<Badge color="dark">Off</Badge>{/if}
							</TableBodyCell>
							<TableBodyCell>
								<div class="flex gap-1">
									<RowAction icon="fa-pen" label="Edit" on:click={() => openEdit(probe)} />
									<RowAction icon="fa-copy" label="Duplicate" on:click={() => openDuplicate(probe)} />
									<RowAction icon="fa-trash" label="Delete" danger on:click={() => remove(probe)} />
								</div>
							</TableBodyCell>
						</TableBodyRow>
						{#if expandedId === probe.id}
							<TableBodyRow>
								<TableBodyCell colspan={8} class="p-4 bg-gray-50 dark:bg-gray-800/50">
									<ProbeMetricsView probeId={probe.id} />
								</TableBodyCell>
							</TableBodyRow>
						{/if}
					{/each}
				</TableBody>
			</Table>
		{/if}
	</Card>
</div>

<Modal bind:open={showModal} title={editing ? 'Edit Probe' : 'New Probe'} size="lg">
	<div class="space-y-4">
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Name</Label><Input bind:value={form.name} placeholder="API" /></div>
			<div><Label class="mb-1">Type</Label>
				<Select bind:value={form.type}>
					<option value="json">JSON metrics</option>
					<option value="http">HTTP status only</option>
					<option value="agent">Stats agent (auto)</option>
				</Select>
			</div>
		</div>
		<div><Label class="mb-1">Target URL</Label><Input bind:value={form.targetUrl} placeholder="https://app.example.com/stats" /></div>
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">HTTP Method</Label><Input bind:value={form.httpMethod} /></div>
			<div><Label class="mb-1">Secret Mode</Label>
				<Select bind:value={form.secretMode}>
					<option value="header">Header</option>
					<option value="bearer">Bearer token</option>
					<option value="query">Query param</option>
				</Select>
			</div>
		</div>
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Secret param name</Label><Input bind:value={form.secretParamName} /></div>
			<div><Label class="mb-1">Secret key {editing ? '(leave blank to keep)' : ''}</Label><Input type="password" bind:value={secretKey} /></div>
		</div>
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Interval (seconds)</Label><Input type="number" bind:value={form.intervalSeconds} /></div>
			<div><Label class="mb-1">Timeout (ms)</Label><Input type="number" bind:value={form.timeoutMs} /></div>
		</div>
		<Toggle bind:checked={form.enabled}>Enabled</Toggle>

		{#if form.type === 'json'}
			<div class="border-t border-gray-200 dark:border-gray-700 pt-4">
				<div class="flex items-center justify-between mb-2">
					<Label>Metrics provided</Label>
					<Button size="xs" color="alternative" on:click={addMetric}><i class="fa-solid fa-plus me-1"></i>Add metric</Button>
				</div>
				<p class="text-xs text-gray-400 mb-3">Each metric's <span class="font-mono">key</span> is either a dot-path into the response (e.g. <span class="font-mono">db.cpu</span> — kept as-is, numbers or strings) or an arithmetic expression over response paths (e.g. <span class="font-mono">100 * mem.used / mem.total</span> — computed to a number). Expressions read the payload, not other metrics. Thresholds that turn metrics into status are configured per service (Services → metrics).</p>

				{#each metrics as metric, mi}
					<div class="grid grid-cols-12 gap-2 items-center mb-2">
						<Input class="col-span-3" size="sm" placeholder="Label (CPU)" bind:value={metric.label} />
						<Input class="col-span-5" size="sm" placeholder="key / expression (db.cpu)" bind:value={metric.key} />
						<Input class="col-span-1" size="sm" placeholder="unit" bind:value={metric.unit} />
						<div class="col-span-2"><Toggle bind:checked={metric.enabled}>On</Toggle></div>
						<Button class="col-span-1" size="xs" color="red" on:click={() => removeMetric(mi)}><i class="fa-solid fa-trash"></i></Button>
					</div>
				{/each}
				{#if metrics.length === 0}<p class="text-xs text-gray-400">No metrics — only availability ($up) and response time are collected.</p>{/if}
			</div>
		{:else if form.type === 'agent'}
			<p class="text-xs text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
				Stats-agent probe: point it at an endpoint running the AbstractWebCommon statistics middleware
				(<span class="font-mono">abstract-stats</span> envelope). Metrics are <strong>discovered automatically</strong>
				from the response — no need to define them here. Use <strong>Test probe</strong> below to preview what it exposes.
			</p>
		{:else}
			<p class="text-xs text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
				HTTP-status probe: any 2xx response is operational. It still collects availability + response time.
			</p>
		{/if}

		{#if editing}
			<div class="border-t border-gray-200 dark:border-gray-700 pt-4">
				<Button size="xs" color="alternative" on:click={runTest} disabled={testRunning}>
					{#if testRunning}<Spinner size="sm" class="mr-1" />{:else}<i class="fa-solid fa-play me-1"></i>{/if}Test probe now
				</Button>

				{#if testError}<p class="mt-2 text-sm text-red-500">{testError}</p>{/if}

				{#if testResult}
					<p class="mt-2 text-sm">
						<span class={statusText(testResult.status)}>{statusLabel(testResult.status)}</span>
						<span class="text-gray-400 ml-2">{testResult.responseTimeMs} ms</span>
						{#if testResult.error}<span class="text-red-500 ml-2">{testResult.error}</span>{/if}
					</p>

					{#if form.type === 'json'}
						<p class="mt-3 mb-1 text-xs font-semibold text-gray-500 uppercase">Mapped metrics</p>
						{#if metrics.filter((m) => m.key).length === 0}
							<p class="text-xs text-gray-400">No metrics defined — add metrics above to map values.</p>
						{:else}
							<table class="w-full text-xs">
								<tbody>
									{#each metrics.filter((m) => m.key) as metric}
										{@const value = testResult.metrics[metric.key]}
										<tr class="border-b border-gray-100 dark:border-gray-700">
											<td class="py-1 pr-2 text-gray-700 dark:text-gray-300">{metric.label || metric.key}</td>
											<td class="py-1 pr-2 text-gray-400 font-mono">{metric.key}</td>
											<td class="py-1 text-right font-mono {typeof value === 'number' || typeof value === 'string' ? 'text-gray-900 dark:text-white' : 'text-amber-500'}">
												{#if typeof value === 'number'}
													{value}{metric.unit ?? ''}
												{:else if typeof value === 'string'}
													{value}
												{:else}
													no value
												{/if}
											</td>
										</tr>
									{/each}
								</tbody>
							</table>
						{/if}
					{/if}

					{#if form.type === 'agent'}
						<p class="mt-3 mb-1 text-xs font-semibold text-gray-500 uppercase">Discovered metrics</p>
						{#if !testResult.discoveredMetrics || testResult.discoveredMetrics.length === 0}
							<p class="text-xs text-gray-400">No metrics discovered — is this a stats-agent endpoint?</p>
						{:else}
							<table class="w-full text-xs">
								<tbody>
									{#each testResult.discoveredMetrics as metric}
										{@const value = testResult.metrics[metric.key]}
										<tr class="border-b border-gray-100 dark:border-gray-700">
											<td class="py-1 pr-2 text-gray-700 dark:text-gray-300">{metric.label}</td>
											<td class="py-1 pr-2 text-gray-400 font-mono">{metric.key}</td>
											<td class="py-1 text-right font-mono {typeof value === 'number' || typeof value === 'string' ? 'text-gray-900 dark:text-white' : 'text-amber-500'}">
												{#if typeof value === 'number'}
													{value}{metric.unit ?? ''}
												{:else if typeof value === 'string'}
													{value}
												{:else}
													no value
												{/if}
											</td>
										</tr>
									{/each}
								</tbody>
							</table>
							<p class="mt-2 text-xs text-gray-400">These are saved automatically when the probe runs.</p>
						{/if}
					{/if}

					<p class="mt-3 mb-1 text-xs font-semibold text-gray-500 uppercase">Raw response</p>
					<pre class="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded max-h-40 overflow-auto">{JSON.stringify(testResult.rawStats, null, 2)}</pre>
				{/if}
			</div>
		{/if}

		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>

	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={save} disabled={isSaving}>
				{#if isSaving}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>
